// 파일 전송(설계서 §9.4) — **보낼 파일의 출처**를 한 인터페이스로 감싼다.
//
//  - 브라우저: 붙여넣기(`clipboardData`)·끌어다 놓기(`dataTransfer`)의 파일/폴더. 폴더는 `webkitGetAsEntry()` 로 재귀한다.
//  - 통합앱: 네이티브가 등록한 경로 핸들(앱 쪽 구현 — 같은 FileSource 인터페이스).
// 채널(`channel.ts`)은 출처가 어디인지 모른 채 `entries` 와 `read()` 만 쓴다.
import { FILE_ERR, FileError, MAX_ENTRIES, type Entry } from './protocol';

export interface FileSource {
  /** 보낼 항목(순번 = 위치, 폴더 포함). */
  readonly entries: Entry[];
  /** 파일 `i` 의 [offset, offset+len) 바이트. */
  read(i: number, offset: number, len: number): Promise<Uint8Array>;
  /** 건너뛴 바로 가기·링크 수(통합앱 네이티브 출처 — 알림에 덧붙인다). */
  readonly skipped?: number;
  /**
   * 한 번에 읽을 크기(없으면 채널 기본 1 MiB). 통합앱 네이티브는 읽기 한 번이 IPC 왕복이라 크게 읽는다
   * (실측 2026-09-15: 1 MiB 29.7 MB/s → 4 MiB 65 MB/s).
   */
  readonly blockSize?: number;
  /** 보내기가 끝나면(성공·실패·취소) 부른다 — 통합앱 네이티브 핸들을 놓는다. */
  dispose?(): void;
}

/** 브라우저 File 객체들로 만든 출처. 폴더 항목은 파일이 없다(null). */
export class BrowserFileSource implements FileSource {
  private constructor(
    readonly entries: Entry[],
    private readonly files: (File | null)[],
  ) {}

  async read(i: number, offset: number, len: number): Promise<Uint8Array> {
    const f = this.files[i];
    if (!f) {
      throw new FileError(FILE_ERR.PROTOCOL, '폴더를 읽으려 함');
    }
    try {
      return new Uint8Array(await f.slice(offset, offset + len).arrayBuffer());
    } catch {
      throw new FileError(FILE_ERR.LOCAL_READ);
    }
  }

  /** 붙여넣기·끌어다 놓기의 DataTransfer → 출처. 파일이 없으면 null(텍스트·이미지 붙여넣기는 클립보드 경로로). */
  static async fromDataTransfer(dt: DataTransfer | null): Promise<BrowserFileSource | null> {
    if (!dt) {
      return null;
    }
    const builder = new Builder();
    // 항목(item)부터 본다 — 폴더를 알아볼 수 있는 유일한 길(webkitGetAsEntry).
    const items = Array.from(dt.items ?? []).filter((it) => it.kind === 'file');
    const entries = items.map((it) => (typeof it.webkitGetAsEntry === 'function' ? it.webkitGetAsEntry() : null));
    if (items.length > 0 && entries.every((e) => e !== null)) {
      for (const e of entries) {
        await builder.addEntry(e as FileSystemEntry, '');
      }
    } else {
      for (const f of Array.from(dt.files ?? [])) {
        builder.addFile(f, f.name);
      }
    }
    return builder.build();
  }

  /**
   * 파일 패널(내 PC 칸) — 사용자가 고른 폴더 **핸들** 아래의 이름들 → 출처. 폴더는 재귀로 훑는다.
   * 🔴 브라우저는 경로가 아니라 핸들로만 읽는다 — 사용자가 고른 폴더 밖은 손댈 수 없다(그것이 이 칸의 경계다).
   */
  static async fromDirectoryHandles(
    root: FileSystemDirectoryHandle,
    names: string[],
  ): Promise<BrowserFileSource | null> {
    const builder = new Builder();
    for (const name of names) {
      await addHandle(builder, root, name, '');
    }
    return builder.build();
  }

  /**
   * 파일 선택 창(`<input type=file>`)의 결과 → 출처.
   * @param picked 고른 파일과 상대 경로(`pickLocalFiles` — 폴더 선택이면 "폴더/하위/파일")
   * @param folder 폴더 선택(`webkitdirectory`)이었으면 true — 상대 경로로 폴더 구조를 되살린다.
   */
  static fromPicked(picked: PickedFile[], folder: boolean): BrowserFileSource | null {
    const builder = new Builder();
    if (!folder) {
      for (const p of picked) {
        builder.addFile(p.file, p.file.name);
      }
      return builder.build();
    }
    // 폴더 항목을 먼저(부모가 자식보다 앞) — 받는 쪽이 폴더를 만든 뒤 파일을 쓴다. 빈 폴더는 선택 창이 주지 않는다(브라우저 한계).
    const sorted = [...picked].sort((a, b) => (a.rel < b.rel ? -1 : 1));
    const dirs = new Set<string>();
    for (const p of sorted) {
      const parts = p.rel.split('/');
      for (let k = 1; k < parts.length; k++) {
        const d = parts.slice(0, k).join('/');
        if (!dirs.has(d)) {
          dirs.add(d);
          builder.addDir(d);
        }
      }
      builder.addFile(p.file, p.rel);
    }
    return builder.build();
  }
}

/**
 * 파일 선택 창에서 고른 파일 하나.
 * 상대 경로를 따로 드는 이유: 파일 창(브라우저 팝업)이 고른 것을 세션 창으로 `postMessage` 하면 File 은 복제되지만
 * **`webkitRelativePath` 는 따라오지 않는다**(구조화 복제 대상이 아니다) — 고른 창에서 미리 꺼내 둔다.
 */
export interface PickedFile {
  file: File;
  /** 폴더 선택이면 "폴더/하위/파일", 파일 선택이면 파일 이름. */
  rel: string;
}

/**
 * 파일·폴더 선택 창을 연다(**사용자 클릭 안에서** 불러야 한다 — 브라우저 규칙). 부른 창(document)에서 열린다.
 * 창을 그냥 닫으면 빈 목록으로 끝난다(`cancel` 이벤트를 모르는 브라우저에서는 끝나지 않는다 — 기다리는 쪽이 없어 무해).
 */
export function pickLocalFiles(folder: boolean): Promise<PickedFile[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    if (folder) {
      input.webkitdirectory = true;
    }
    input.onchange = () =>
      resolve(Array.from(input.files ?? []).map((file) => ({ file, rel: file.webkitRelativePath || file.name })));
    input.addEventListener('cancel', () => resolve([]));
    input.click();
  });
}

/** 목록을 모은다(순번 부여 · 상한 조기 차단). 경로·크기 규칙은 채널이 보내기 전에 `checkEntries` 로 한 번 더 본다. */
class Builder {
  private readonly entries: Entry[] = [];
  private readonly files: (File | null)[] = [];

  private push(entry: Omit<Entry, 'i'>, file: File | null): void {
    if (this.entries.length >= MAX_ENTRIES) {
      throw new FileError(FILE_ERR.TOO_MANY);
    }
    // 이름을 NFC 로 — macOS 브라우저는 파일 이름을 NFD(한글 첫가끝 분해)로 준다. 그대로 보내면 Windows 에
    // `PLC_ㅇㅓㄷㅡ…` 처럼 자모가 풀린 이름으로 생긴다(2026-10-23 실기). 받는 쪽(rs_files·Android)도 합치지만 보내는 쪽에서 먼저 맞춘다.
    this.entries.push({ i: this.entries.length, ...entry, path: entry.path.normalize('NFC') });
    this.files.push(file);
  }

  addFile(f: File, path: string): void {
    const entry: Omit<Entry, 'i'> = { path, size: f.size, dir: false };
    if (f.lastModified) {
      entry.mtime = f.lastModified;
    }
    this.push(entry, f);
  }

  addDir(path: string): void {
    this.push({ path, size: 0, dir: true }, null);
  }

  async addEntry(e: FileSystemEntry, parent: string): Promise<void> {
    const path = parent ? `${parent}/${e.name}` : e.name;
    if (e.isFile) {
      const file = await new Promise<File>((resolve, reject) =>
        (e as FileSystemFileEntry).file(resolve, () => reject(new FileError(FILE_ERR.LOCAL_READ))),
      );
      this.addFile(file, path);
      return;
    }
    if (!e.isDirectory) {
      return;
    }
    this.push({ path, size: 0, dir: true }, null);
    const children = await readAll((e as FileSystemDirectoryEntry).createReader());
    // 이름순 — 받는 쪽 진행이 탐색기 순서와 비슷하게 흐르도록(에이전트 collect 와 같은 규칙).
    children.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
    for (const c of children) {
      await this.addEntry(c, path);
    }
  }

  build(): BrowserFileSource | null {
    if (this.entries.length === 0) {
      return null;
    }
    // private 생성자 우회 — 같은 파일 안의 조립기만 쓴다.
    return new (BrowserFileSource as unknown as new (e: Entry[], f: (File | null)[]) => BrowserFileSource)(
      this.entries,
      this.files,
    );
  }
}

/**
 * 폴더 핸들 안의 이름 하나를 목록에 넣는다(폴더면 그 아래까지). 파일인지 폴더인지는 **열어 봐야** 안다
 * (핸들 API 에는 "종류만 묻기" 가 없다 — 파일로 먼저 열어 보고, 아니면 폴더로 연다).
 */
async function addHandle(b: Builder, dir: FileSystemDirectoryHandle, name: string, parent: string): Promise<void> {
  const path = parent ? `${parent}/${name}` : name;
  let file: FileSystemFileHandle | null = null;
  try {
    file = await dir.getFileHandle(name);
  } catch {
    file = null;
  }
  if (file) {
    b.addFile(await file.getFile(), path);
    return;
  }
  const sub = await dir.getDirectoryHandle(name);
  b.addDir(path);
  const children = await handleNames(sub);
  // 이름순 — 받는 쪽 진행이 탐색기 순서와 비슷하게 흐르도록(에이전트 collect·DataTransfer 경로와 같은 규칙).
  children.sort((a, c) => (a < c ? -1 : a > c ? 1 : 0));
  for (const c of children) {
    await addHandle(b, sub, c, path);
  }
}

/** 폴더 핸들 안의 이름들(타입 선언에 `keys()` 가 없는 TS 판을 위해 캐스팅한다). */
export async function handleNames(dir: FileSystemDirectoryHandle): Promise<string[]> {
  const it = (dir as unknown as { keys(): AsyncIterable<string> }).keys();
  const out: string[] = [];
  for await (const name of it) {
    out.push(name);
  }
  return out;
}

/** 폴더 읽기는 한 번에 최대 100개씩 준다 — 빈 배열이 올 때까지 반복해야 전부다. */
async function readAll(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  const all: FileSystemEntry[] = [];
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((resolve, reject) =>
      reader.readEntries(resolve, () => reject(new FileError(FILE_ERR.LOCAL_READ))),
    );
    if (batch.length === 0) {
      return all;
    }
    all.push(...batch);
  }
}

/** 이 DataTransfer 에 파일이 들어 있는가(붙여넣기·끌어다 놓기에서 파일 경로로 갈지 판단). */
export function hasFiles(dt: DataTransfer | null): boolean {
  if (!dt) {
    return false;
  }
  if (Array.from(dt.types ?? []).includes('Files')) {
    return true;
  }
  return Array.from(dt.items ?? []).some((it) => it.kind === 'file');
}
